import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from '@theme/ThemeContext';
import { AuthProvider } from '@contexts/AuthContext';
import { LoginModalProvider } from '@contexts/LoginModalContext';
import LoginModal from '@components/modals/LoginModal';
import SessionTimeout from '@components/auth/SessionTimeout';
import ScrollToTop from '@components/shared/ScrollToTop';
import GtmRouteTracker from '@components/analytics/GtmRouteTracker';
import { store, persistor } from './store';
import Layout from '@components/layout/Layout';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import PublicRoute from '@components/auth/PublicRoute';
import Home from '@pages/Home';
import About from '@pages/About';
import Assessment from '@pages/Assessment';
import AssessmentHistory from '@pages/AssessmentHistory';
import Results from '@pages/Results';
import FAQ from '@pages/FAQ';
import News from '@pages/News';
import AuthCallback from '@pages/AuthCallback';
import useAuthSession from '@hooks/useAuthSession';
import Spinner from '@components/ui/Spinner';
import { trackGtmButtonClick } from '@/analytics/gtm';
import { lightColors } from '@theme/colors';
import './index.css';

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Spinner size="xl" text="Loading application..." showText={true} color={lightColors.primary} usePrimaryColor={false} />
  </div>
);

const AuthComponents = () => (
  <>
    <LoginModal />
    <SessionTimeout />
  </>
);

const AppRoutes = () => {
  const { isInitializing } = useAuthSession();

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" text="Loading application..." showText={true} usePrimaryColor={true} />
      </div>
    );
  }

  return (
    <LoginModalProvider>
      <Router>
        <ScrollToTop />
        <GtmRouteTracker />
        <AuthComponents />
        <Layout>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              <PublicRoute>
                <Home />
              </PublicRoute>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/results" element={<Results />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Protected routes - require authentication */}
            <Route element={<ProtectedRoute />}>
              <Route path="/assessment/history" element={<AssessmentHistory />} />
              <Route path="/news" element={<News />} />
            </Route>

            {/* Auth callback */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Access denied page */}
            <Route path="/access-denied" element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
                  <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
                  <button
                    onClick={() => {
                      trackGtmButtonClick({
                        button_id: 'page_access_denied_go_back',
                        section: 'page_access_denied',
                        label: 'Go Back',
                      });
                      window.history.back();
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            } />
          </Routes>
        </Layout>
      </Router>
    </LoginModalProvider>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={<LoadingSpinner />} persistor={persistor}>
        <ThemeProvider defaultMode="light">
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
